import { AxiosResponse } from 'axios'
import { IFilter } from 'types/common'
import { api } from 'API'
import { INotificationWithRelations } from 'interfaces/notification'

/**
 * GET /users/me/notifications — account-keyed list for the current user.
 * Backend owns the filter (types, includes, ordering); client only passes pagination.
 * Use this for the bell + notification page — everything that needs the user's full
 * cross-org notification stream.
 */
export async function getMyNotifications(limit = 10, skip = 0): Promise<INotificationWithRelations[]> {
  const response: AxiosResponse = await api.get(`/users/me/notifications?limit=${limit}&skip=${skip}`)
  return response.data
}

/**
 * GET /notifications — generic filter endpoint, kept for specialized use cases that
 * need ad-hoc queries (e.g. processStore looking up common-step-update notifications
 * for a specific processId). Prefer {@link getMyNotifications} for the user's stream.
 */
export async function getNotifications(
  filter: IFilter<INotificationWithRelations> = {}
): Promise<INotificationWithRelations[]> {
  const response: AxiosResponse = await api.get(`/notifications?filter=${JSON.stringify(filter)}`)
  return response.data
}

export async function countNotifications(): Promise<number> {
  const response: AxiosResponse = await api.get(`/users/me/notifications/count-unseen`)
  return response?.data?.count ?? 0
}

export async function seenAllNotifications(): Promise<void> {
  const response = await api.patch(`/users/me/notifications/seen-all`)
  return response.data
}

export async function seenNotification(notificationId: number): Promise<void> {
  if (!notificationId) return
  await api.patch(`/notifications/${notificationId}`, { isSeen: true })
}

/**
 * GET /notifications/counts?profiles=athlete-1,coach-2,...
 * Batch endpoint (added in Phase 2) — returns unread count per profile key.
 * Used by SelectProfilePage to show per-profile badges without loading full lists.
 */
export async function getNotificationCountsForProfiles(
  profileKeys: string[]
): Promise<Record<string, number>> {
  const response: AxiosResponse<Record<string, number>> = await api.get('/notifications/counts', {
    params: { profiles: profileKeys.join(',') }
  })
  return response.data
}
